import type { View } from '../router/router';
import { apiClient } from '../services/api-client';
import { authService } from '../services/auth.service';
import { toast } from '../components/layout/toast';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface PaymentStatus {
  status: 'ACTIVE' | 'OVERDUE' | 'EXPIRED' | 'NONE';
  nextDueDate?: string; // ISO date
  overdueDays?: number;
}

interface PaymentRecord {
  id: string;
  amount: number;
  paymentDate: string; // ISO
  status: 'PAID' | 'PENDING' | 'OVERDUE';
}

export class PaymentsPage implements View {
  private status: PaymentStatus | null = null;
  private history: PaymentRecord[] = [];
  private loading = true;
  private errorMsg: string | null = null;

  async render(): Promise<string> {
    await this.fetchData();

    if (this.loading) {
      return `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 60vh; gap: 1rem;">
          <div style="color: var(--tyris-energy); animation: spin 1s linear infinite;">
            ${getTyrisIcon('en-curso', { size: 36 })}
          </div>
          <p style="color: var(--text-muted);">Cargando información de pagos...</p>
        </div>
      `;
    }

    if (this.errorMsg) {
      return `
        <div class="card-glass" style="text-align: center; padding: 3rem 1.5rem; margin-top: 2rem;">
          <div style="color: var(--color-error); margin-bottom: 1rem;">
            ${getTyrisIcon('alerta', { size: 48 })}
          </div>
          <h3 style="font-size: 1.5rem; margin-bottom: 0.5rem;">Error al obtener datos de pagos</h3>
          <p style="color: var(--text-muted); margin-bottom: 1.5rem;">${this.errorMsg}</p>
          <button onclick="window.location.reload()" class="btn btn-primary" style="width: auto; padding: 0.75rem 2rem;">Reintentar</button>
        </div>
      `;
    }

    const status = this.status ?? { status: 'NONE' };
    const badgeInfo = this.getBadgeInfo(status);

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <h2 style="display:flex;align-items:center;gap:0.6rem;">${getTyrisIcon('pagos', { size: 28 })} Mi Membresía & Pagos</h2>
          <p>Controla tu estado de suscripción y revisa el historial de transacciones.</p>
        </div>
      </div>

      <!-- Tarjeta de Estado de Membresía -->
      <div class="card-glass" style="margin-bottom: 2rem; padding: 1.75rem; display: flex; flex-direction: column; gap: 1rem;">
        <div style="display: flex; align-items: center; gap: 1rem;">
          <span class="badge" style="${badgeInfo.style}">${badgeInfo.label}</span>
          <h3 style="font-size: 1.3rem; margin: 0;">${status.status}</h3>
        </div>
        ${status.nextDueDate ? `
          <p style="color: var(--text-muted);">
            Próximo vencimiento: <strong>${new Date(status.nextDueDate).toLocaleDateString('es-ES', { timeZone: 'UTC' })}</strong>
          </p>
        ` : ''}
        ${status.overdueDays ? `
          <p style="color: var(--color-error); font-weight: 600;">
            Días de retraso: <strong>${status.overdueDays}</strong>
          </p>
        ` : ''}
      </div>

      <!-- Historial de Pagos -->
      <h3 style="font-size: 1.35rem; margin-bottom: 1rem; font-weight: 600;">Historial de Pagos</h3>
      <div class="card-glass" style="overflow-x:auto;">
        <table style="width:100%; border-collapse:collapse; font-size:0.9rem;">
          <thead>
            <tr style="border-bottom:1px solid var(--border-metallic); background:rgba(255,255,255,0.02);">
              <th style="padding:0.85rem 1rem; color:var(--text-muted); font-weight:600;">Fecha</th>
              <th style="padding:0.85rem 1rem; color:var(--text-muted); font-weight:600;">Importe</th>
              <th style="padding:0.85rem 1rem; color:var(--text-muted); font-weight:600;">Estado</th>
            </tr>
          </thead>
          <tbody>
            ${this.history.map(rec => `
              <tr style="border-bottom:1px solid rgba(255,255,255,0.03);">
                <td style="padding:0.75rem 1rem;">${new Date(rec.paymentDate).toLocaleDateString('es-ES', { timeZone: 'UTC' })}</td>
                <td style="padding:0.75rem 1rem; font-weight:600; color:var(--color-primary);">$${rec.amount.toFixed(2)}</td>
                <td style="padding:0.75rem 1rem; color:${rec.status === 'PAID' ? 'var(--color-success)' : rec.status === 'PENDING' ? 'var(--color-warning)' : 'var(--color-error)'}; font-weight:600;">${rec.status}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  private getBadgeInfo(status: PaymentStatus): { label: string; style: string } {
    switch (status.status) {
      case 'ACTIVE':
        return { label: 'Activo', style: 'background-color: rgba(20,195,142,0.15); color: hsl(160,95%,45%); border:1px solid rgba(20,195,142,0.3);' };
      case 'OVERDUE':
        return { label: 'Vencido', style: 'background-color: rgba(255,69,0,0.15); color: hsl(15,95%,55%); border:1px solid rgba(255,69,0,0.3);' };
      case 'EXPIRED':
        return { label: 'Expirado', style: 'background-color: rgba(220,53,69,0.15); color: hsl(350,80%,65%); border:1px solid rgba(220,53,69,0.3);' };
      default:
        return { label: 'Sin Membresía', style: 'background-color: rgba(255,255,255,0.05); color: var(--text-muted);' };
    }
  }

  private async fetchData(): Promise<void> {
    try {
      const apiStatus = await apiClient.get<any>('/api/payments/my-status');
      
      let mappedStatus: 'ACTIVE' | 'OVERDUE' | 'EXPIRED' | 'NONE' = 'NONE';
      if (apiStatus.isActive) {
        mappedStatus = 'ACTIVE';
      } else if (apiStatus.latestPayment) {
        if (apiStatus.latestPayment.status === 'OVERDUE' || (apiStatus.daysUntilDue !== null && apiStatus.daysUntilDue < 0)) {
          mappedStatus = 'OVERDUE';
        } else {
          mappedStatus = 'EXPIRED';
        }
      }

      this.status = {
        status: mappedStatus,
        nextDueDate: apiStatus.nextDueDate || undefined,
        overdueDays: apiStatus.daysUntilDue && apiStatus.daysUntilDue < 0 ? Math.abs(apiStatus.daysUntilDue) : undefined,
      };

      const user = authService.getUser();
      if (user?.id) {
        this.history = await apiClient.get<PaymentRecord[]>(`/api/payments/student/${user.id}`);
      } else {
        this.history = [];
      }
      this.errorMsg = null;
    } catch (err: any) {
      console.error('Error fetching payments data:', err);
      this.errorMsg = err.message || 'No se pudo conectar al servidor.';
      toast.error('Error al cargar información de pagos.');
    } finally {
      this.loading = false;
    }
  }
}
